import React, { useMemo, useCallback } from 'react';
import {
  Grid,
  Typography,
  Divider,
  makeStyles,
  Theme,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';

// Types
import { DisplayItem } from '../../../types';

const useStyles = makeStyles((theme: Theme) => ({
  desktopTitleContainer: {
    margin: theme.spacing(2),
    marginBottom: 0,
  },
  cardRow: {
    marginTop: theme.spacing(1),
  },
  cardRowTextLeft: {
    [theme.breakpoints.up('md')]: {
      marginRight: theme.spacing(2),
    },
  },
  cardRowTextRight: {
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(2),
    },
  },
  separator: {},
}));

type ListContentDividedType = {
  contentItems: DisplayItem[];
  adjustToDesktop?: boolean;
};

const ListContentDivided = ({
  contentItems,
  adjustToDesktop = false,
}: ListContentDividedType): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mdColSize = useMemo(() => (adjustToDesktop ? 6 : 12), [
    adjustToDesktop,
  ]);

  const makeRow = useCallback(
    (
      text: React.ReactNode,
      value: string,
      offset: number,
      max: number,
    ): React.ReactElement => {
      let dividerElement = (
        <Grid item xs={12}>
          <Divider className={classes.separator} />
        </Grid>
      );
      let noDividerOffset = max % 2 === 0 ? 2 : 1;

      if (isMobile || !adjustToDesktop) {
        noDividerOffset = 1;
      }

      if (offset >= max - noDividerOffset) {
        dividerElement = <></>;
      }
      return (
        <Grid
          key={offset}
          item
          xs={12}
          md={mdColSize}
          className={classes.cardRow}
        >
          <Grid container spacing={1}>
            <Grid
              item
              xs={4}
              md={5}
              container
              justifyContent="flex-start"
              alignItems="center"
            >
              <Typography
                variant="caption"
                className={
                  offset % 2 !== 0 && adjustToDesktop
                    ? classes.cardRowTextRight
                    : ''
                }
              >
                {value}
              </Typography>
            </Grid>
            <Grid
              item
              xs={8}
              md={7}
              container
              justifyContent="flex-end"
              alignItems="center"
            >
              {typeof text !== 'string' && <> {text} </>}
              {typeof text === 'string' && (
                <Typography
                  variant="body2"
                  className={
                    offset % 2 === 0 && adjustToDesktop
                      ? classes.cardRowTextLeft
                      : ''
                  }
                >
                  {text}
                </Typography>
              )}
            </Grid>
            {dividerElement}
          </Grid>
        </Grid>
      );
    },
    [classes, isMobile, mdColSize],
  );

  return (
    <>
      <Grid container spacing={0} alignItems="flex-end">
        {contentItems.map(
          (row, index): React.ReactElement =>
            makeRow(row.Text, row.Value, index, contentItems.length),
        )}
      </Grid>
    </>
  );
};

export default ListContentDivided;
